// src/components/Homescreen3DPub.tsx

import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import * as THREE from 'three';
import { getPubRaycasting } from '../API';
import MultiMeshViewer from './MultiMeshViewer';
import { convertFromExternalCoords } from '../utils/threeUtils';

interface Homescreen3DPubProps {
  googlePlaceId: string;
  onError?: () => void;
}

interface SceneData {
  meshUris: string[];
  gardenPolygons: { [key: string]: { polygon: [number, number][], elevation: number | null } };
  sunVectors: { [timestamp:string]: [number, number, number] };
  focalPoint: [number, number, number];
}

const Homescreen3DPub: React.FC<Homescreen3DPubProps> = ({ googlePlaceId, onError }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sceneData, setSceneData] = useState<SceneData | null>(null);
  const [animatedSunVector, setAnimatedSunVector] = useState<[number, number, number] | undefined>();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const raycastResponse = await getPubRaycasting(googlePlaceId, true);
        const { data } = raycastResponse;

        if (!data.mesh_uris || data.mesh_uris.length === 0 || !data.session_id || !data.sun_vector || !data.garden_polygons) {
          throw new Error("API response is missing required data for 3D preview.");
        }
        
        const allPoints: [number, number][] = [];
        let totalElevation = 0;
        let elevationCount = 0;

        Object.values(data.garden_polygons as SceneData['gardenPolygons']).forEach(garden => {
            if (garden.polygon) allPoints.push(...garden.polygon);
            if (garden.elevation !== null) {
                totalElevation += garden.elevation;
                elevationCount++;
            }
        });

        if (allPoints.length === 0) {
            throw new Error("Cannot determine focal point: No garden points available.");
        }

        const avgLat = allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length;
        const avgLon = allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length;
        const avgElevation = elevationCount > 0 ? totalElevation / elevationCount : 0;
        
        const focalPointEcef = convertFromExternalCoords([avgLat, avgLon, avgElevation]);
        const focalPoint: [number, number, number] = [focalPointEcef.x, focalPointEcef.y, focalPointEcef.z];
        
        const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!googleTilesApiKey) {
            throw new Error("Google Maps API key is not configured.");
        }

        const completeMeshUris = data.mesh_uris.map((uri: string) => {
            const uriPath = uri.startsWith('/') ? uri : `/${uri}`;
            const cleanSessionId = data.session_id.startsWith('session=') ? data.session_id.substring(8) : data.session_id;
            return `https://tile.googleapis.com${uriPath}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
        });
        
        const newSceneData: SceneData = {
          meshUris: completeMeshUris,
          gardenPolygons: data.garden_polygons,
          sunVectors: data.sun_vector,
          focalPoint: focalPoint,
        };
        setSceneData(newSceneData);

      } catch (err: any) {
        setError(err.message || "Could not load 3D preview.");
        if (onError) onError();
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [googlePlaceId, onError]);

  useEffect(() => {
    if (!sceneData?.sunVectors) return;

    const timeKeys = Object.keys(sceneData.sunVectors).sort();
    if (timeKeys.length === 0) return;

    let currentIndex = 0;
    let direction = 1;
    let progress = 0;
    let animationId: number;
    const animationSpeed = 0.03;
    
    const animate = () => {
      const currentTime = timeKeys[currentIndex];
      const nextIndex = Math.min(currentIndex + 1, timeKeys.length - 1);
      const nextTime = timeKeys[nextIndex];
      
      const currentSunVector = new THREE.Vector3(...sceneData.sunVectors[currentTime]);
      const nextSunVector = new THREE.Vector3(...sceneData.sunVectors[nextTime]);
      const interpolatedVector = new THREE.Vector3().lerpVectors(currentSunVector, nextSunVector, progress);
      
      setAnimatedSunVector(interpolatedVector.toArray() as [number, number, number]);
      
      progress += animationSpeed * direction;
      if (progress >= 1) {
        progress = 0;
        currentIndex += 1;
        if (currentIndex >= timeKeys.length - 1) {
          direction = -1;
          currentIndex = timeKeys.length - 1;
        }
      } else if (progress <= 0 && direction === -1) {
        progress = 1;
        currentIndex -= 1;
        if (currentIndex <= 0) {
          direction = 1;
          currentIndex = 0;
        }
      }
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    return () => { cancelAnimationFrame(animationId); };
  }, [sceneData]);


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error || !sceneData) {
    console.error("Error loading 3D pub:", error);
    return null;
  }

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100%', 
      height: '100%',
      pointerEvents: 'none',
      '& canvas': {
        pointerEvents: 'none'
      }
    }}>
      <MultiMeshViewer
        meshUris={sceneData.meshUris}
        focalPoint={sceneData.focalPoint}
        gardenPolygons={sceneData.gardenPolygons}
        sunEcefVector={animatedSunVector}
        height="100%"
        initialZoom={50}
      />
    </Box>
  );
};

export default Homescreen3DPub;